import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../api";

/**
 * Per-venue S7000 Cloudflare Tunnel. Same mechanism as the IGT panel, but
 * S7000 exposes a SINGLE WebAPI endpoint (default HTTP :8444), so one hostname.
 * Provision the tunnel here, run the generated installer once at the venue, and
 * the venue's S7000 Base URL points at the tunnel automatically.
 */
const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
const CLOUDFLARED_MSI = "https://github.com/cloudflare/cloudflared/releases/latest/download/cloudflared-windows-amd64.msi";

function CopyField({ value, mono = true }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
      <code style={{ flex: 1, minWidth: 0, overflowX: "auto", whiteSpace: "nowrap", padding: "0.5rem 0.7rem", background: "var(--surface-2, #0f172a10)", border: "1px solid var(--border)", borderRadius: 8, fontFamily: mono ? "monospace" : "inherit", fontSize: "0.82rem" }}>{value}</code>
      <button type="button" className="btn btn-outline btn-sm" onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = (status || "unknown").toLowerCase();
  const color = s === "healthy" ? "var(--success)" : s === "degraded" ? "var(--warning)" : s === "down" || s === "inactive" ? "var(--danger)" : "var(--text-muted)";
  return <span style={{ display: "inline-block", padding: "0.1rem 0.55rem", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color, border: `1px solid ${color}` }}>{status || "unknown"}</span>;
}

export default function S7000TunnelPanel({ venueId, venueName, onChanged }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subdomainBase: "", originIp: "", port: 8444, domain: "circlescan.app" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const s = await adminApi.getS7000Tunnel(venueId);
      setState(s);
      setForm((f) => ({ ...f, subdomainBase: f.subdomainBase || slugify(venueName) }));
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [venueId, venueName]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const provision = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const r = await adminApi.provisionS7000Tunnel(venueId, form);
      setResult(r);
      onChanged?.();
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Delete this tunnel? The venue's S7000 connection stops working until re-provisioned.")) return;
    setBusy(true); setError("");
    try { await adminApi.deleteS7000Tunnel(venueId); setResult(null); onChanged?.(); await load(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };

  const installCommand = result?.installCommand || state?.installCommand;
  const label = { fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" };

  const instructions = installCommand && (
    <div style={{ marginTop: "1rem" }}>
      <div className="form-section-label">Install the connector at the venue</div>
      <ol style={{ margin: "0.25rem 0 0", paddingLeft: "1.2rem", fontSize: "0.9rem", lineHeight: 1.7 }}>
        <li>On a machine at the venue that can reach the S7000 box, download <a href={CLOUDFLARED_MSI} target="_blank" rel="noreferrer">cloudflared (Windows .msi)</a> and run the installer.</li>
        <li>Open <strong>Command Prompt / PowerShell as Administrator</strong> and run:</li>
      </ol>
      <div style={{ marginTop: "0.5rem" }}><CopyField value={installCommand} /></div>
      <p className="step-sub" style={{ marginTop: "0.5rem" }}>Then the status above turns <strong>Healthy</strong>. This command carries a live token — treat it as a secret.</p>
    </div>
  );

  return (
    <div style={{ maxWidth: 760, marginTop: "1.5rem" }}>
      <h3 style={{ margin: "0 0 0.25rem" }}>Cloudflare Tunnel</h3>
      <p style={{ color: "var(--text-muted)", marginTop: 0, fontSize: "0.9rem", lineHeight: 1.5 }}>
        If the club's S7000 sits on its private LAN, bridge to it with a Cloudflare tunnel. Provision it here,
        then run the generated installer once at the venue. The S7000 Base URL points at the tunnel automatically.
      </p>

      {error && <div className="info-box error" style={{ color: "var(--danger)", marginBottom: "0.75rem" }}><span>{error}</span></div>}

      {loading ? <div style={{ color: "var(--text-muted)" }}>Loading…</div> : !state?.configured && (
        <div className="info-box" style={{ marginBottom: "1rem" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <span>Cloudflare isn't configured yet — add the account token in <strong>Settings → Cloudflare</strong> (token needs Account: Cloudflare Tunnel Edit + Zone: DNS Edit).</span>
        </div>
      )}

      {!loading && state?.provisioned ? (
        <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1.1rem 1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "0.85rem" }}>
            <strong>Tunnel provisioned</strong>
            {state.status && <StatusBadge status={state.status.status} />}
            <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: "auto" }} onClick={load} disabled={busy}>Refresh</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.7rem 1.25rem" }}>
            <div><div style={label}>WebAPI hostname</div><div style={{ fontSize: "0.88rem" }}>{state.hostname}</div></div>
            <div><div style={label}>Origin</div><div style={{ fontSize: "0.88rem" }}>{state.originIp}:{state.port}</div></div>
          </div>
          {instructions}
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.1rem" }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={provision} disabled={busy || !state.configured}>{busy ? "Working…" : "Re-provision"}</button>
            <button type="button" className="btn btn-outline btn-sm" style={{ color: "var(--danger)" }} onClick={remove} disabled={busy}>Delete tunnel</button>
          </div>
        </div>
      ) : !loading && (
        <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1.1rem 1.25rem" }}>
          <div className="form-grid-2">
            <div className="form-field">
              <label>Subdomain base</label>
              <input value={form.subdomainBase} onChange={set("subdomainBase")} placeholder="campsie" />
              <span className="step-sub" style={{ fontSize: "0.75rem" }}>→ {slugify(form.subdomainBase) || "…"}-s7000.{form.domain}</span>
            </div>
            <div className="form-field">
              <label>Domain (zone)</label>
              <input value={form.domain} onChange={set("domain")} placeholder="circlescan.app" />
            </div>
          </div>
          <div className="form-grid-2">
            <div className="form-field">
              <label>S7000 origin IP (on the venue LAN)</label>
              <input value={form.originIp} onChange={set("originIp")} placeholder="10.0.0.50" />
            </div>
            <div className="form-field">
              <label>WebAPI port</label>
              <input type="number" value={form.port} onChange={set("port")} placeholder="8444" style={{ width: 100 }} />
            </div>
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            <button type="button" className="btn btn-primary" onClick={provision} disabled={busy || !state?.configured || !form.subdomainBase || !form.originIp || !form.domain}>
              {busy ? "Creating tunnel…" : "Create tunnel"}
            </button>
          </div>
          {result && instructions}
        </div>
      )}
    </div>
  );
}
